import { useState, useEffect, useCallback, useRef } from "preact/hooks";
import { classOverridesToCSS, type CustomizerState } from "./types";

const STORAGE_KEY = "festapp:css-customizer-draft";
const STYLE_ID = "festapp-customizer-injected";
const DEBOUNCE_MS = 500;

function emptyState(): CustomizerState {
  return { classOverrides: {} };
}

function loadDraft(): CustomizerState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.version === 1) {
        return {
          classOverrides: parsed.classOverrides || {},
        };
      }
    }
  } catch {
    // Ignore corrupt data
  }
  return emptyState();
}

function saveDraft(state: CustomizerState) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...state, version: 1 })
    );
  } catch {
    // Storage full or unavailable
  }
}

function getOrCreateStyleEl(): HTMLStyleElement {
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement("style");
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  return el;
}

export function useCustomizerState() {
  const [state, setState] = useState<CustomizerState>(() => loadDraft());
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Apply class overrides via style element (persists even when panel is closed)
  useEffect(() => {
    const styleEl = getOrCreateStyleEl();
    styleEl.textContent = classOverridesToCSS(state.classOverrides, true);

    return () => {
      styleEl.textContent = "";
      styleEl.remove();
    };
  }, [state.classOverrides]);

  // Debounced save to localStorage
  useEffect(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveDraft(state), DEBOUNCE_MS);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [state]);

  const setClassProperty = useCallback(
    (className: string, prop: string, value: string) => {
      setState((prev) => ({
        ...prev,
        classOverrides: {
          ...prev.classOverrides,
          [className]: {
            ...prev.classOverrides[className],
            [prop]: value,
          },
        },
      }));
    },
    []
  );

  const resetClassProperty = useCallback(
    (className: string, prop: string) => {
      setState((prev) => {
        const clsProps = { ...prev.classOverrides[className] };
        delete clsProps[prop];
        const next = { ...prev.classOverrides };
        if (Object.keys(clsProps).length === 0) {
          delete next[className];
        } else {
          next[className] = clsProps;
        }
        return { ...prev, classOverrides: next };
      });
    },
    []
  );

  const resetAll = useCallback(() => {
    setState(emptyState());
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore
    }
    const styleEl = document.getElementById(STYLE_ID);
    if (styleEl) {
      styleEl.textContent = "";
      styleEl.remove();
    }
  }, []);

  const generateExport = useCallback((): string => {
    const lines: string[] = [];
    lines.push("/* FestApp Widget - Custom Styles */");
    lines.push("/* Paste into WordPress > Appearance > Custom CSS */");
    lines.push("");

    const classCSS = classOverridesToCSS(state.classOverrides, true);
    if (classCSS) {
      lines.push(classCSS);
    }

    return lines.join("\n") + "\n";
  }, [state]);

  const hasChanges = Object.keys(state.classOverrides).length > 0;

  return {
    state,
    setClassProperty,
    resetClassProperty,
    resetAll,
    generateExport,
    hasChanges,
  };
}
